import React, { useId, useMemo, useState } from "react";
import { Megaphone } from "lucide-react";
import FormLabel from "../FormLabel";
import { ReadableNavButton } from "../Sticker";
import { FieldHint } from "../createEvent/CreateEventFormUi";
import { ReadablePillToggle } from "../ui/FormSection";
import NotifyRow from "../dashboard/NotifyRow";

/** Flip when POST class broadcast API ships. Until then, never enable Send. */
export const CLASS_BROADCAST_AVAILABLE = false;

const MESSAGE_MAX_LENGTH = 500;

const FILTERS = [
  { id: "all", label: "All Contacts" },
  { id: "regulars", label: "Regulars" },
  { id: "past", label: "Not Booked" },
  { id: "followers", label: "Followers" },
];

function formatFollowerMeta(follower) {
  const bits = [];
  if (follower.booked_this_session) bits.push("booked this session");
  else bits.push("not booked");
  if (follower.visit_count > 0) {
    bits.push(`${follower.visit_count} visit${follower.visit_count === 1 ? "" : "s"}`);
    if (follower.last_session) bits.push(`last ${follower.last_session}`);
  }
  if (follower.phone) bits.push(follower.phone);
  else bits.push("no phone");
  return bits.join(" · ");
}

function followerSegments(follower) {
  const segments = ["followers"];
  if ((follower.visit_count || 0) >= 2) segments.push("regulars");
  if (!follower.booked_this_session) segments.push("past");
  return segments;
}

function emptyPrompt(filterId) {
  if (filterId === "regulars") return "No regulars in this follower list yet.";
  if (filterId === "past") return "Everyone following has booked this session.";
  if (filterId === "followers") return "No followers yet. Dancers can follow from the class page when they RSVP.";
  return "No contacts to reach yet.";
}

export default function BroadcastMessageTab({
  sessionLabel,
  expectedCount = 0,
  followers = [],
  followersLoading = false,
}) {
  const [message, setMessage] = useState("");
  const [filter, setFilter] = useState("all");
  const statusId = useId();
  const hintId = useId();
  const broadcastReady = CLASS_BROADCAST_AVAILABLE;
  const trimmed = message.trim();
  const canSend = broadcastReady && trimmed.length > 0 && trimmed.length <= MESSAGE_MAX_LENGTH && expectedCount > 0;

  const contacts = useMemo(
    () =>
      (followers || []).map((f) => ({
        ...f,
        segments: followerSegments(f),
      })),
    [followers],
  );

  const counts = useMemo(() => {
    const next = { all: contacts.length, regulars: 0, past: 0, followers: contacts.length };
    for (const c of contacts) {
      if (c.segments.includes("regulars")) next.regulars += 1;
      if (c.segments.includes("past")) next.past += 1;
    }
    return next;
  }, [contacts]);

  const visibleFilters = useMemo(
    () => FILTERS.filter((f) => f.id === "all" || counts[f.id] > 0),
    [counts],
  );

  const activeFilter = visibleFilters.some((f) => f.id === filter) ? filter : "all";

  const filtered = useMemo(() => {
    if (activeFilter === "all") return contacts;
    return contacts.filter((c) => c.segments.includes(activeFilter));
  }, [contacts, activeFilter]);

  function handleSend(e) {
    e.preventDefault();
    if (!canSend) return;
    // Wired when CLASS_BROADCAST_AVAILABLE + API exist.
  }

  return (
    <div className="max-w-page mx-auto px-4 mt-6 space-y-10 pb-8" data-testid="class-dash-broadcast-tab">
      <section className="space-y-4" aria-labelledby="class-broadcast-title" data-testid="broadcast-panel">
        <header className="space-y-1">
          <h2 id="class-broadcast-title" className="font-ui font-semibold text-[18px] sm:text-[20px] leading-tight">
            Broadcast App Notification
          </h2>
          <p className="font-ui font-medium text-[14px] text-ink/70 leading-snug">
            Sends a push / in-app notification to students booked for this date
            {expectedCount > 0 ? ` (${expectedCount})` : ""}.
          </p>
        </header>

        {!broadcastReady ? (
          <p
            id={statusId}
            role="status"
            className="font-ui font-medium text-[14px] text-ink/65"
            data-testid="class-dash-broadcast-unavailable"
          >
            In-app broadcast isn&apos;t available yet. Ping people on WhatsApp in the list below for now.
          </p>
        ) : expectedCount === 0 ? (
          <p className="font-ui font-medium text-[14px] text-ink/65" role="status">
            No bookings for this date yet. Share your class link to get started.
          </p>
        ) : (
          <FieldHint id={hintId}>
            Message all students booked for {sessionLabel || "this date"}.
          </FieldHint>
        )}

        <label className="block">
          <FormLabel className="block mb-2">Message</FormLabel>
          <textarea
            className="field w-full min-h-[120px] resize-y disabled:opacity-60 disabled:cursor-not-allowed"
            value={message}
            onChange={(e) => setMessage(e.target.value.slice(0, MESSAGE_MAX_LENGTH))}
            placeholder="e.g. Doors open 30 minutes early tonight!"
            maxLength={MESSAGE_MAX_LENGTH}
            disabled={!broadcastReady || expectedCount === 0}
            aria-disabled={!broadcastReady || expectedCount === 0}
            aria-describedby={!broadcastReady ? statusId : hintId}
            data-testid="class-dash-broadcast-message"
          />
          <p className="font-ui text-[12px] font-medium text-ink/55 mt-1 tabular-nums">
            {message.length} / {MESSAGE_MAX_LENGTH}
          </p>
        </label>

        <ReadableNavButton
          type="button"
          variant="primary"
          className="w-full sm:w-auto"
          disabled={!canSend}
          aria-disabled={!canSend}
          aria-describedby={!broadcastReady ? statusId : undefined}
          onClick={handleSend}
          data-testid="class-dash-broadcast-send"
        >
          <Megaphone className="w-4 h-4" strokeWidth={2.5} aria-hidden />
          Send update
        </ReadableNavButton>
      </section>

      <section className="space-y-4" aria-labelledby="class-reach-out-contacts-title">
        <header className="space-y-1">
          <h2 id="class-reach-out-contacts-title" className="font-ui font-semibold text-[18px] sm:text-[20px] leading-tight">
            Re-engage & Invite Dancers
          </h2>
          <p className="font-ui font-medium text-[14px] text-ink/70 leading-snug">
            Reach out to regulars and followers who haven&apos;t booked this session yet.
          </p>
        </header>

        <div className="space-y-2" data-testid="class-followers-list">
          {followersLoading ? (
            <p className="font-ui font-medium text-[14px] text-ink/60" role="status" aria-live="polite">
              Loading contacts…
            </p>
          ) : counts.all === 0 ? (
            <p className="font-ui font-medium text-[14px] text-ink/60">
              No followers yet. Dancers can follow from the class page when they RSVP.
            </p>
          ) : (
            <>
              <div
                className="flex gap-2 flex-wrap"
                role="group"
                aria-label="Contact filters"
                data-testid="reach-out-filter-chips"
              >
                {visibleFilters.map((f) => (
                  <ReadablePillToggle
                    key={f.id}
                    active={activeFilter === f.id}
                    onClick={() => setFilter(f.id)}
                    aria-pressed={activeFilter === f.id}
                    data-testid={`reach-out-filter-${f.id}`}
                  >
                    {f.label}
                    <span className="tabular-nums text-ink/55">· {counts[f.id]}</span>
                  </ReadablePillToggle>
                ))}
              </div>

              {filtered.length === 0 ? (
                <p className="font-ui font-medium text-[14px] text-ink/60">{emptyPrompt(activeFilter)}</p>
              ) : (
                filtered.map((f) => (
                  <NotifyRow
                    key={f.organizer_id}
                    name={f.name}
                    meta={formatFollowerMeta(f)}
                    whatsappUrl={f.whatsapp_url}
                    testId={`class-follower-${f.organizer_id}`}
                    pingTestId={`ping-class-follower-${f.organizer_id}-btn`}
                  />
                ))
              )}
            </>
          )}
        </div>
      </section>
    </div>
  );
}
